import { useMemo } from 'react';
import { useNodes } from '@/hooks/data/useNodes';
import { useHttpStatsOverview, useHttpStatsTimeseries } from '@/hooks/data/useHttpStats';
import { useStatsFilters } from '@/hooks/useStatsFilters';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { ErrorAlert, PageHeader } from '@/components/PageParts';
import { CollectorsStrip } from '@/components/stats/CollectorsStrip';
import { StatsTotals } from '@/components/stats/StatsTotals';
import { SitesStatsTable } from '@/components/stats/SitesStatsTable';
import { DomainStatsModal } from '@/components/stats/DomainStatsModal';
import { StatsFreshness } from '@/components/stats/StatsFreshness';
import { describeStatsRange, STATS_RANGES, statsRangeHasTimeChart } from '@/config/httpStats';
import { nodeHasCapability } from '@/config/nodeTypes';
import { formatTimeOfDay } from '@/utils/date';
import './Stats.css';

/**
 * Per-site HTTP status statistics: totals, a table of sites with their error
 * rates and a drill-down per site. Filters live in the URL; the data
 * refreshes while the tab is visible (every 15 seconds for the short ranges,
 * every minute otherwise) and ends with the last minute all nodes reported.
 */
export function Stats() {
  const { range, nodeId, domain, setRange, setNodeId, closeDomain, getDomainHref } = useStatsFilters();
  const filters = { range, nodeId: nodeId || undefined };

  const { summary, collectors, updatedAt, dataUntil, isLoading, isFetching, error, reload } =
    useHttpStatsOverview(filters);
  const drilldown = useHttpStatsTimeseries(domain, filters);

  // Only nodes running nginx report statistics
  const { nodes } = useNodes();
  const nodeOptions = useMemo(
    () =>
      nodes
        .filter((node) => nodeHasCapability(node, 'hasNginx'))
        .map((node) => ({ value: node.id, label: node.name })),
    [nodes]
  );

  const isRefreshing = isFetching && summary !== null;

  return (
    <div className="stats-page">
      <PageHeader
        classPrefix="stats"
        title="Statistics"
        actions={
          <div className="stats-header-actions">
            {updatedAt && (
              <span className="stats-updated" aria-live="polite">
                Updated {formatTimeOfDay(updatedAt)}
              </span>
            )}
            <Button variant="secondary" onClick={() => void reload()} isLoading={isFetching}>
              Refresh
            </Button>
          </div>
        }
      />

      <div className="stats-filters">
        <SegmentedControl label="Time range" value={range} options={STATS_RANGES} onChange={setRange} />
        <Select
          value={nodeId}
          onChange={setNodeId}
          placeholder="All nodes"
          options={nodeOptions}
          className="stats-filter-select"
          aria-label="Filter by node"
        />
        <StatsFreshness dataUntil={dataUntil} />
      </div>

      <ErrorAlert classPrefix="stats" message={error} onRetry={() => void reload()} />

      <CollectorsStrip collectors={collectors} />

      <div className={`stats-content ${isRefreshing ? 'stats-content-refreshing' : ''}`.trim()} aria-busy={isFetching}>
        {summary && (
          <StatsTotals requests={summary.totals.requests} statusClasses={summary.totals.status_classes} />
        )}
        {(summary || isLoading) && (
          <SitesStatsTable
            domains={summary?.domains ?? []}
            isLoading={isLoading}
            getDomainHref={getDomainHref}
          />
        )}
      </div>

      <DomainStatsModal
        domain={domain}
        rangeDescription={describeStatsRange(range)}
        showTimeChart={statsRangeHasTimeChart(range)}
        timeseries={drilldown.timeseries}
        isLoading={drilldown.isLoading}
        error={drilldown.error}
        onClose={closeDomain}
      />
    </div>
  );
}
